/**
 * Responsive modal manager (tray vs centered dialog).
 */
import { BREAKPOINTS, isMobile } from '../utility/index.js';

const SELECTORS = {
	MODAL: '[data-responsive-modal]',
	TRIGGER: '[data-responsive-modal-trigger]',
	CLOSE: '[data-responsive-modal-close]',
};

const CLASSES = {
	MODAL_ACTIVE: 'responsive-modal--active',
	MODAL_TRAY: 'responsive-modal--tray',
	BODY_ACTIVE: 'body--responsive-modal-active',
};

class ResponsiveModalManager {
	constructor() {
		this.modals = new Map();
		this.activeModal = null;

		this.handleTriggerClick = this.handleTriggerClick.bind(this);
		this.handleDocumentClick = this.handleDocumentClick.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);

		this.init();
	}

	init() {
		this.collectModals();
		if (this.modals.size === 0) return;

		this.bindTriggers();
		this.bindCloseButtons();

		document.addEventListener('click', this.handleDocumentClick, {
			passive: true,
		});
		document.addEventListener('keydown', this.handleKeydown, {
			passive: true,
		});
	}

	collectModals() {
		const modalElements = document.querySelectorAll(SELECTORS.MODAL);
		modalElements.forEach((modal) => {
			const id = modal.getAttribute('data-responsive-modal-id');
			if (!id) return;

			this.modals.set(id, modal);
		});
	}

	bindTriggers() {
		const triggers = document.querySelectorAll(SELECTORS.TRIGGER);
		triggers.forEach((trigger) => {
			trigger.addEventListener('click', this.handleTriggerClick);
		});
	}

	bindCloseButtons() {
		const closeButtons = document.querySelectorAll(SELECTORS.CLOSE);
		closeButtons.forEach((button) => {
			button.addEventListener('click', () => {
				this.closeActiveModal();
			});
		});
	}

	handleTriggerClick(event) {
		const trigger = event.currentTarget;
		const targetId = trigger.getAttribute('data-responsive-modal-trigger');
		if (!targetId) return;

		event.preventDefault();
		// Stop bubbling so document-level listeners (e.g. click-outside-to-close)
		// do not run on the same click that opened the modal. Do not use
		// stopImmediatePropagation — it blocks other click listeners on this
		// element (e.g. Segment / GenericTracking on `[data-responsive-modal-trigger]`).
		event.stopPropagation();

		const modal = this.modals.get(targetId);
		if (!modal) return;

		this.openModal(modal);
	}

	handleDocumentClick(event) {
		if (!this.activeModal) return;

		const dialog = this.activeModal.querySelector('.responsive-modal__dialog');
		if (!dialog) return;

		if (!dialog.contains(event.target)) {
			this.closeActiveModal();
		}
	}

	handleKeydown(event) {
		if (!this.activeModal) return;
		if (event.key !== 'Escape') return;

		this.closeActiveModal();
	}

	/**
	 * Close other header-driven UI (interstitial, mobile CTA, nav menus)
	 * when opening a responsive modal, to avoid overlapping layers.
	 */
	closeOtherHeaderUi() {
		const header = window.headerInstance;
		if (!header) return;

		// Close header interstitial if active (desktop overlay)
		if (
			header.isInterstitialActive &&
			typeof header.boundCloseInterstitial === 'function'
		) {
			header.boundCloseInterstitial();
		}

		// Close sticky mobile CTA if active
		if (
			header.isMobileCtaActive &&
			typeof header.boundCloseMobileCta === 'function'
		) {
			header.boundCloseMobileCta();
		}

		// Close any active mobile sub-menus
		if (header.boundCloseAllSubMenus) {
			header.boundCloseAllSubMenus();
		}

		// If a Bricks hamburger/nav toggle is active, simulate a click to let Bricks close it cleanly.
		const hamburgerToggle = document.querySelector(
			'.header__nav-item--hamburger.active .brx-submenu-toggle button, .header__nav-item--hamburger.active .brx-submenu-toggle',
		);
		if (hamburgerToggle) {
			hamburgerToggle.click();
		}

		// Close any open desktop sub-menu accordions by clicking their toggles.
		const openDesktopToggles = document.querySelectorAll(
			'.header__link--toggle.active .brx-submenu-toggle button, .header__link--sub-menu-accordion.active .brx-submenu-toggle button',
		);
		openDesktopToggles.forEach((btn) => {
			btn.click();
		});
	}

	openModal(modal) {
		if (this.activeModal === modal) return;

		// Close any currently active modal first.
		if (this.activeModal) {
			this.closeActiveModal();
		}

		// Ensure other header modals/nav menus are closed first, especially on desktop.
		this.closeOtherHeaderUi();

		this.activeModal = modal;
		modal.removeAttribute('hidden');

		// On mobile, use tray behavior; on desktop, ensure tray styles are not applied.
		if (isMobile(BREAKPOINTS.HEADER_DESKTOP)) {
			modal.classList.add(CLASSES.MODAL_TRAY);
		} else {
			modal.classList.remove(CLASSES.MODAL_TRAY);
		}

		modal.classList.add(CLASSES.MODAL_ACTIVE);
		document.body.classList.add(CLASSES.BODY_ACTIVE);
	}

	closeActiveModal() {
		if (!this.activeModal) return;

		const closedId = this.activeModal.getAttribute('data-responsive-modal-id');

		this.activeModal.classList.remove(CLASSES.MODAL_ACTIVE);
		this.activeModal = null;

		document.body.classList.remove(CLASSES.BODY_ACTIVE);

		if (closedId) {
			document.dispatchEvent(
				new CustomEvent('growtherapyResponsiveModalClosed', {
					detail: { id: closedId },
				}),
			);
		}
	}

	/**
	 * Open a modal by its data-responsive-modal-id (e.g. for WPML browser-language notice).
	 *
	 * @param {string} id - Modal ID set on the element via data-responsive-modal-id.
	 * @return {boolean} True if a modal was opened.
	 */
	openById(id) {
		if (!id || typeof id !== 'string') return false;
		const modal = this.modals.get(id);
		if (!modal) return false;
		this.openModal(modal);
		return true;
	}
}

document.addEventListener('DOMContentLoaded', () => {
	const manager = new ResponsiveModalManager();
	window.growtherapyResponsiveModalManager = manager;
	document.dispatchEvent(
		new CustomEvent('growtherapyResponsiveModalReady', {
			detail: { manager },
		}),
	);
});
